/**
 * Reiter „Sicherheit" der Konto-Seite: Zwei-Faktor (Spec 4) und
 * Passwortwechsel. Beides gehoert dem angemeldeten Menschen und niemandem
 * sonst — deshalb steht es unter „Mein Konto" und nicht im Admin-Bereich.
 *
 * Die Benachrichtigungen (Spec 8.5) standen frueher mit auf dieser Seite und
 * sind jetzt ein eigener Reiter (`AccountNotifications`).
 *
 * Zwei-Faktor-Selbstverwaltung (TODO aus Phase 0, Spec 4).
 *
 * Ablauf, wie Better-Auth ihn vorgibt (`skipVerificationOnEnable: false`):
 *   1. `twoFactor.enable({ password })` → TOTP-URI + Backup-Codes
 *   2. QR scannen, Code eingeben, `twoFactor.verifyTotp({ code })`
 *      → erst jetzt gilt 2FA als aktiv
 *   3. `twoFactor.disable({ password })` schaltet es wieder ab
 *
 * Die Backup-Codes werden **einmalig** angezeigt — dieselbe Regel wie fuer
 * API-Keys (Spec 11). Passwort-Hashing, TOTP-Secret und Codes gehoeren
 * vollstaendig Better-Auth; hier wird nichts selbst gerechnet.
 */
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, totpCodeSchema } from '@tickethub/shared';
import { QRCodeSVG } from 'qrcode.react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';
import { useSession } from '@/lib/session';

type Step = 'idle' | 'confirm';

export function AccountSecurity() {
  const { t } = useTranslation();
  const { user, reload } = useSession();

  const [step, setStep] = useState<Step>('idle');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Eigener Satz Felder fuer den Passwortwechsel — das Formular oben benutzt
  // `password` als Bestaetigung fuer 2FA und hat damit nichts zu tun.
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordPending, setPasswordPending] = useState(false);

  async function startEnable(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setPending(true);

    try {
      const result = await authClient.twoFactor.enable({ password });
      if (result.error) {
        setFormError(t('security.wrong_password'));
        return;
      }
      setTotpUri(result.data.totpURI);
      setBackupCodes(result.data.backupCodes);
      setPassword('');
      setStep('confirm');
    } finally {
      setPending(false);
    }
  }

  async function confirmEnable(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = totpCodeSchema.safeParse({ code, trust_device: false });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? t('security.wrong_code'));
      return;
    }

    setPending(true);
    try {
      const result = await authClient.twoFactor.verifyTotp({ code: parsed.data.code });
      if (result.error) {
        setFormError(t('security.wrong_code'));
        return;
      }
      setCode('');
      setTotpUri(null);
      setStep('idle');
      await reload();
    } finally {
      setPending(false);
    }
  }

  async function disable(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setPending(true);

    try {
      const result = await authClient.twoFactor.disable({ password });
      if (result.error) {
        setFormError(t('security.wrong_password'));
        return;
      }
      setPassword('');
      setBackupCodes([]);
      await reload();
    } finally {
      setPending(false);
    }
  }

  /**
   * Passwortwechsel (Better-Auth-Core, `POST /api/auth/change-password`).
   *
   * `revokeOtherSessions: true` ist Absicht: wer sein Passwort wechselt, tut
   * das im Zweifel, weil das alte kompromittiert ist — dann muessen alle
   * anderen Sitzungen sofort weg (security.md). Die eigene bleibt bestehen,
   * Better-Auth setzt dafuer ein frisches Session-Cookie.
   */
  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(t('security.password_too_short', { count: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword.length > MAX_PASSWORD_LENGTH) {
      setPasswordError(t('security.password_too_long', { count: MAX_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== repeatPassword) {
      setPasswordError(t('security.password_mismatch'));
      return;
    }

    setPasswordPending(true);
    try {
      const result = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true,
      });
      if (result.error) {
        setPasswordError(
          result.error.code === 'INVALID_PASSWORD'
            ? t('security.wrong_current_password')
            : t('security.password_change_failed'),
        );
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
      toast.success(t('security.password_changed'));
    } finally {
      setPasswordPending(false);
    }
  }

  async function regenerateBackupCodes(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setPending(true);

    try {
      const result = await authClient.twoFactor.generateBackupCodes({ password });
      if (result.error) {
        setFormError(t('security.wrong_password'));
        return;
      }
      setBackupCodes(result.data.backupCodes);
      setPassword('');
    } finally {
      setPending(false);
    }
  }

  const enabled = user?.two_factor_enabled === true;

  return (
    <div className="max-w-xl space-y-6">
      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="text-lg font-medium">{t('security.two_factor')}</h2>
        <p className="text-sm text-muted-foreground">
          {enabled ? t('security.status_enabled') : t('security.status_disabled')}
        </p>

        {step === 'confirm' && totpUri ? (
          <form className="space-y-4" onSubmit={(event) => void confirmEnable(event)}>
            <p className="text-sm">{t('security.scan_hint')}</p>
            <div className="inline-block rounded-md bg-white p-3">
              <QRCodeSVG value={totpUri} size={176} aria-label={t('security.qr_alt')} />
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer">{t('security.manual_entry')}</summary>
              <code className="mt-1 block break-all rounded bg-muted px-2 py-1 text-xs">
                {totpUri}
              </code>
            </details>

            <div className="space-y-2">
              <Label htmlFor="totp-code">{t('security.code')}</Label>
              <Input
                id="totp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              />
            </div>

            {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
            <Button type="submit" disabled={pending}>
              {pending ? t('common.loading') : t('security.confirm')}
            </Button>
          </form>
        ) : enabled ? (
          <form className="space-y-4" onSubmit={(event) => void disable(event)}>
            <div className="space-y-2">
              <Label htmlFor="disable-password">{t('security.password')}</Label>
              <Input
                id="disable-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
            <div className="flex gap-2">
              <Button type="submit" variant="destructive" disabled={pending}>
                {t('security.disable')}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={(event) => void regenerateBackupCodes(event)}
              >
                {t('security.new_backup_codes')}
              </Button>
            </div>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={(event) => void startEnable(event)}>
            <div className="space-y-2">
              <Label htmlFor="enable-password">{t('security.password')}</Label>
              <Input
                id="enable-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
            <Button type="submit" disabled={pending}>
              {pending ? t('common.loading') : t('security.enable')}
            </Button>
          </form>
        )}
      </section>

      {/* ── Passwort ändern ────────────────────────────────────────────── */}
      <section
        className="space-y-3 rounded-lg border p-4"
        aria-labelledby="change-password-heading"
      >
        <h2 className="text-lg font-medium" id="change-password-heading">
          {t('security.change_password')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('security.change_password_hint', { count: MIN_PASSWORD_LENGTH })}
        </p>

        <form className="space-y-4" onSubmit={(event) => void changePassword(event)}>
          <div className="space-y-2">
            <Label htmlFor="current-password">{t('security.password')}</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="change-new-password">{t('security.new_password')}</Label>
            <Input
              id="change-new-password"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="change-repeat-password">{t('security.repeat_password')}</Label>
            <Input
              id="change-repeat-password"
              type="password"
              autoComplete="new-password"
              required
              value={repeatPassword}
              onChange={(event) => setRepeatPassword(event.target.value)}
            />
          </div>

          {passwordError ? <p className="text-sm text-destructive">{passwordError}</p> : null}
          <Button type="submit" disabled={passwordPending}>
            {passwordPending ? t('common.saving') : t('security.save_password')}
          </Button>
        </form>
      </section>

      {backupCodes.length > 0 ? (
        <Alert>
          <AlertTitle>{t('security.backup_codes')}</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{t('security.backup_hint')}</p>
            <ul className="grid grid-cols-2 gap-1 font-mono text-xs">
              {backupCodes.map((backupCode) => (
                <li key={backupCode}>{backupCode}</li>
              ))}
            </ul>
            <Button className="mt-2" size="sm" variant="outline" onClick={() => setBackupCodes([])}>
              {t('common.close')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
